import { useState, useRef, useEffect } from 'react';
import { DateRange } from 'react-date-range';
import { es } from 'date-fns/locale';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import moment from 'moment';

function formatDate(date) {
  if (!date) return '';
  const d = (date instanceof Date) ? date : new Date(date);
  if (isNaN(d)) return '';
  return d.toLocaleDateString('es-ES');
}

const DateRangeSelector = ({
  startDate,
  endDate,
  onChange,
  label = '',
  error = false,
  helperText = '',
  minDate,
  maxDate,
  disabled = false,
  isOneWay = false,
  months = 2,
  departureDate,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  // Cerrar el popup si se hace click fuera
  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Handler para selección de fechas
  const handleDateChange = (ranges) => {
    const { startDate: newStart, endDate: newEnd } = ranges.selection;
    if (isOneWay) {
      onChange({ startDate: newStart, endDate: null });
      setOpen(false);
    } else {
      // Si es la fecha de ida y no hay fecha de vuelta, usar la misma fecha
      if (newStart && !newEnd) {
        onChange({ startDate: newStart, endDate: newStart });
      } else {
        onChange({ startDate: newStart, endDate: newEnd });
        // Cerrar el date picker cuando se selecciona manualmente la fecha de vuelta
        if (newEnd && newEnd !== newStart) {
          setOpen(false);
        }
      }
    }
  };

  // Convertir departureDate a Date si existe
  const minDateFromDeparture = departureDate ? moment.utc(departureDate).toDate() : minDate;

  // Si hay departureDate y no hay startDate, usar departureDate como startDate y endDate
  useEffect(() => {
    if (departureDate && !startDate && !isOneWay) {
      const departureDateObj = moment.utc(departureDate).toDate();
      onChange({ startDate: departureDateObj, endDate: departureDateObj });
    }
  }, [departureDate, startDate, isOneWay, onChange]);

  return (
    <div style={{ position: 'relative', width: '100%' }} ref={ref}>
      <label style={{ fontSize: 12, color: '#4f4f4f', display: 'block', marginBottom: 4 }}>{label}</label>
      <div
        className={`form__field date-picker ${error ? 'dates-error' : ''}`}
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          height: 40,
          lineHeight: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: error ? '1px solid rgb(207, 24, 0)' : '1px solid #ccc',
          borderRadius: 4,
          padding: '0 12px',
          background: disabled ? '#f5f5f5' : '#fff',
          gap: 8,
          flexDirection: 'row'
        }}
        onClick={() => !disabled && setOpen(!open)}
      >
        {isOneWay ? (
          <span style={{ color: !startDate ? '#aaa' : '#222', whiteSpace: 'nowrap', fontSize: 16, display: 'inline-block' }}>
            {startDate ? formatDate(startDate) : 'Ida'}
          </span>
        ) : (
          <>
            <span style={{ color: !startDate ? '#aaa' : '#222', whiteSpace: 'nowrap', fontSize: 16, display: 'inline-block' }}>
              {startDate ? formatDate(startDate) : 'Ida'}
            </span>
            <span style={{ color: '#aaa', fontSize: 18, display: 'inline-block' }}>→</span>
            <span style={{ color: !endDate ? '#aaa' : '#222', whiteSpace: 'nowrap', fontSize: 16, display: 'inline-block' }}>
              {endDate ? formatDate(endDate) : 'Vuelta'}
            </span>
          </>
        )}
      </div>
      {helperText && error && (
        <div style={{ color: 'rgb(207, 24, 0)', fontSize: 11, marginTop: 2 }}>{helperText}</div>
      )}
      {open && (
        <div style={{ position: 'absolute', zIndex: 10, top: 48, left: 0, boxShadow: '0 2px 8px #0002' }}>
          <DateRange
            ranges={[
              {
                startDate: startDate || new Date(),
                endDate: isOneWay ? startDate || new Date() : endDate || startDate || new Date(),
                key: 'selection',
              },
            ]}
            onChange={handleDateChange}
            locale={es}
            minDate={minDateFromDeparture}
            maxDate={maxDate}
            showMonthAndYearPickers={true}
            rangeColors={['#1976d2']}
            direction="horizontal"
            months={months || 2}
            disabledDates={[]}
            retainEndDateOnFirstSelection={false}
          />
        </div>
      )}
    </div>
  );
};

export default DateRangeSelector; 